import type { BusinessProfile, Currency } from "../types";
import { slugify } from "../lib";

export type AuthIntent = "profile" | "request";

export type PaymentRequestDraft = {
  amount: string;
  currency: string;
  title: string;
  productName: string;
  bundleName: string;
  discountOffer: string;
  offerDetails: string;
  reference: string;
  customerName: string;
  customerContact: string;
  expiresAt: string;
  internalNote: string;
};

export const GUEST_DRAFT_KEY = "lucaden.guestDraft";
export const AUTH_INTENT_KEY = "lucaden.authIntent";
export const PENDING_REQUEST_KEY = "lucaden.pendingRequestDraft";

function readLocalJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const value = window.localStorage.getItem(key);
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

function writeLocalJson<T>(key: string, value: T) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
}

export function clearLocalValue(key: string) {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(key);
  }
}

export function readGuestDraft() {
  return readLocalJson<BusinessProfile | null>(GUEST_DRAFT_KEY, null);
}

export function saveGuestDraft(profile: BusinessProfile) {
  writeLocalJson(GUEST_DRAFT_KEY, profile);
}

export function readAuthIntent() {
  return readLocalJson<AuthIntent | null>(AUTH_INTENT_KEY, null);
}

export function saveAuthIntent(intent: AuthIntent) {
  writeLocalJson(AUTH_INTENT_KEY, intent);
}

export function readPendingRequestDraft() {
  return readLocalJson<PaymentRequestDraft | null>(PENDING_REQUEST_KEY, null);
}

export function savePendingRequestDraft(request: PaymentRequestDraft) {
  writeLocalJson(PENDING_REQUEST_KEY, request);
}

export function buildGuestProfileDraft(input: {
  businessName: string;
  category: string;
  fpsProxyType: BusinessProfile["fpsProxyType"];
  fpsProxyValue: string;
  expectedRecipientName: string;
  paymentRemarksInstruction: string;
  currency?: Currency;
}): BusinessProfile {
  const previous = readGuestDraft();
  const businessName = input.businessName.trim() || "My business";
  return {
    id: previous?.id || "",
    ownerEmail: previous?.ownerEmail,
    businessName,
    slug: previous?.businessName === businessName && previous.slug ? previous.slug : slugify(businessName) || "my-business",
    category: input.category || previous?.category || "Other",
    district: previous?.district || "Central and Western",
    shortDescription: previous?.shortDescription || "FPS-ready business profile.",
    longDescription: previous?.longDescription || "",
    contactPerson: previous?.contactPerson || "",
    whatsapp: previous?.whatsapp || "",
    phone: previous?.phone || "",
    email: previous?.email || "",
    website: previous?.website || "",
    instagram: previous?.instagram || "",
    address: previous?.address || "",
    logoDataUrl: previous?.logoDataUrl || "",
    languagePreference: previous?.languagePreference || "Both",
    directoryVisible: previous?.directoryVisible || false,
    status: "draft",
    verificationLevel: previous?.verificationLevel || 0,
    fpsProxyType: input.fpsProxyType,
    fpsProxyValue: input.fpsProxyValue.trim(),
    currency: input.currency || previous?.currency || "HKD",
    expectedRecipientName: input.expectedRecipientName.trim(),
    bankName: previous?.bankName || "",
    paymentRemarksInstruction: input.paymentRemarksInstruction.trim() || "Use your invoice or order number in FPS remarks.",
    defaultPaymentNote: previous?.defaultPaymentNote || "",
    hideRawProxy: previous?.hideRawProxy || false,
    showCopyButton: previous?.showCopyButton ?? true,
    updatedAt: new Date().toISOString()
  };
}
